import { CALENDAR_MONTH_KEY_REGEX } from 'components/Common/Calendar/calendarFocusMonth'
import { ISO_DATE_REGEX, clampToMonthRange, getDateFromMonthKey, isValidDate, startOfMonth } from 'lib/datetime/dateUtils'

type CalendarDateInput = Date | string | null | undefined

/**
 * Parses the itinerary's `YYYY-MM` travel-month hint into the first day of that
 * month in local time. Parsing the parts by hand avoids `new Date('YYYY-MM')`,
 * which is interpreted as UTC and can land in the wrong month west of GMT.
 *
 * Returns undefined for anything that is not exactly `YYYY-MM` with a real month.
 */
export function parseIndicativeSearchMonth(value?: string | null): Date | undefined {
  if (!value) return undefined

  const match = CALENDAR_MONTH_KEY_REGEX.exec(value)
  if (!match) return undefined

  const date = getDateFromMonthKey(value)
  // Reject years that JS silently remaps (0–99 → 1900–1999).
  if (!isValidDate(date) || date.getFullYear() !== Number(match[1])) return undefined

  return date
}

function toLocalMonthStart(value: CalendarDateInput): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) return isValidDate(value) ? startOfMonth(value) : undefined

  const match = ISO_DATE_REGEX.exec(value)
  if (!match) return undefined

  const month = Number(match[2])
  if (month < 1 || month > 12) return undefined

  return getDateFromMonthKey(`${match[1]}-${match[2]}`)
}

export interface ResolveSearchCalendarFocusDateInput {
  /** Start date the traveller has explicitly picked on this search surface. */
  selectedStart?: CalendarDateInput
  /** Concrete itinerary date relevant to this surface, e.g. the stop's start date. */
  relevantConcreteStart?: CalendarDateInput
  /** The itinerary's loose `YYYY-MM` travel-month hint. */
  indicativeSearchMonth?: string | null
  /** Earliest month this surface allows the traveller to pick. */
  minDate?: Date
  /** Latest month this surface allows the traveller to pick. */
  maxDate?: Date
  /** Overridable for deterministic tests. */
  now?: Date
}

/**
 * Picks which month a search calendar should open on, as the first day of that
 * month in local time.
 *
 * This is a viewport hint only. The returned date must never be written back as
 * a check-in, check-out or departure date — an indicative month says roughly
 * when someone wants to travel, not which nights they intend to stay.
 *
 * Precedence, most to least specific:
 * 1. the traveller's own selection on this surface
 * 2. a concrete itinerary date relevant to this surface
 * 3. the itinerary's indicative travel month
 * 4. today
 *
 * The result is clamped into `[minDate, maxDate]` so a hint that has fallen out
 * of range lands on the nearest selectable month rather than snapping back to today.
 */
export function resolveSearchCalendarFocusDate({
  selectedStart,
  relevantConcreteStart,
  indicativeSearchMonth,
  minDate,
  maxDate,
  now,
}: ResolveSearchCalendarFocusDateInput): Date {
  const focusMonth = toLocalMonthStart(selectedStart) ??
    toLocalMonthStart(relevantConcreteStart) ??
    parseIndicativeSearchMonth(indicativeSearchMonth) ??
    startOfMonth(now ?? new Date())

  return clampToMonthRange(focusMonth, minDate, maxDate)
}
